import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Users, ArrowLeft } from "lucide-react";

const SIDE_IMG = "https://images.unsplash.com/photo-1758518730136-1bf4fa26ccbf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "", state: "", sponsor_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const urlRef = params.get("ref");
    if (urlRef) localStorage.setItem("afha_ref", urlRef.toUpperCase());
    const ref = urlRef || localStorage.getItem("afha_ref");
    if (ref) setForm((f) => ({ ...f, sponsor_code: ref.toUpperCase() }));
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Welcome to AFHA!");
      navigate("/activate");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <Link to="/" data-testid="explore-website" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-patriot transition-colors">
            <ArrowLeft className="h-4 w-4" /> Explore the AFHA website & plan first
          </Link>
          <h1 className="font-display text-3xl text-navy tracking-tight mt-4">Become a Representative</h1>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-navy">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="signup-name" className="mt-1.5 h-11" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="email" className="text-navy">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email" className="mt-1.5 h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-navy">Phone number</Label>
              <Input id="phone" type="tel" required value={form.phone} onChange={set("phone")} data-testid="signup-phone" className="mt-1.5 h-11" placeholder="(555) 000-0000" />
            </div>
            <div>
              <Label htmlFor="password" className="text-navy">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password" className="mt-1.5 h-11" placeholder="At least 6 characters" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city" className="text-navy">City</Label>
                <Input id="city" value={form.city} onChange={set("city")} data-testid="signup-city" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="state" className="text-navy">State</Label>
                <Input id="state" value={form.state} onChange={set("state")} data-testid="signup-state" className="mt-1.5 h-11" />
              </div>
            </div>
            <div>
              <Label htmlFor="sponsor" className="text-navy">Sponsor code <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="sponsor" value={form.sponsor_code} onChange={set("sponsor_code")} data-testid="signup-sponsor" className="mt-1.5 h-11 uppercase" placeholder="e.g. JORDAN1" />
            </div>
            {error && <p className="text-sm text-patriot" data-testid="signup-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full h-11 rounded-full bg-patriot hover:bg-patriot-dark text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-navy hover:underline" data-testid="go-login">Log in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative bg-navy-deep afha-grain order-1 lg:order-2">
        <div className="absolute inset-0 stripes-bg opacity-50" />
        <img src={SIDE_IMG} alt="Professional team" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex justify-end"><Link to="/"><Logo variant="light" /></Link></div>
          <div>
            <Users className="h-10 w-10 text-patriot-light mb-5" />
            <h2 className="font-display text-4xl leading-tight tracking-tight">Bring Advanced Genetic Testing to more families.</h2>
            <p className="mt-4 text-white/70 max-w-md">Join AFHA and help physicians offer GeneID testing to their patients.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
